"use client"

import { Loader2 } from "lucide-react"
import { JSX, memo, useCallback } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInputProps, splitAriaProps } from "../../utils/ComponentProps"
import { TIcon } from "../display/TIcon"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../shadcn/select"
import { IDropdownProps, ISelectionProps } from "./types"

/**
 * 셀렉트 박스 컴포넌트
 *
 * @description
 * 드롭다운 셀렉트 컴포넌트입니다.
 * items는 반드시 { value, label, disabled? } 구조여야 합니다.
 *
 * @template V - 값 타입 (string 또는 number)
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <TSelect
 *   items={[
 *     { value: 1, label: "Option 1" },
 *     { value: 2, label: "Option 2" }
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   placeholder="선택하세요"
 * />
 *
 * // 커스텀 객체를 items로 변환
 * <TSelect
 *   items={users.map(u => ({ value: u.id, label: u.name }))}
 *   value={selectedId}
 *   onChange={setSelectedId}
 * />
 *
 * // renderValue로 선택된 값 커스텀 렌더링
 * <TSelect
 *   items={options}
 *   value={selected}
 *   onChange={setSelected}
 *   renderValue={(value) => <CustomBadge value={value} />}
 * />
 *
 * // 에러 메시지 표시
 * <TSelect
 *   items={options}
 *   value={selected}
 *   onChange={setSelected}
 *   error={true}
 *   errorMessage="필수 항목입니다"
 * />
 * ```
 */
interface TSelectProps<V extends string | number> extends ISelectionProps<V>, IDropdownProps, BaseInputProps {
	/**
	 * 셀렉트 박스 크기
	 * @default "small"
	 * @ai-style "small" → 높이 32px(h-8), text-base
	 * @ai-style "medium" → 높이 40px(h-10), text-base
	 */
	size?: "small" | "medium"
	/** 테두리 없는 스타일 적용 여부 */
	borderless?: boolean
	/** 드롭다운 메뉴(SelectContent)에 적용할 className — 예: 최대 높이 제한 */
	contentClassName?: string
	/** 선택된 값의 커스텀 렌더링 함수 */
	renderValue?: (value: V) => React.ReactNode
	/** 에러 메시지 */
	errorMessage?: string
}

function TSelectInner<V extends string | number>(props: TSelectProps<V>): JSX.Element {
	const {
		// 기본 속성
		id,
		name,
		className,
		contentClassName,

		// 데이터 관련
		items,
		value,
		renderValue,

		// 상태 관련
		disabled = false,
		loading = false,
		required = false,
		error = false,
		readOnly = false,

		// UI 관련
		size = "small",
		borderless = false,
		placeholder,
		errorMessage,

		// 드롭다운 설정
		open,
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 2,

		// 이벤트 핸들러
		onChange,
		onOpenChange,

		// 기타
		dataAttributes,
		...restProps
	} = props

	const dataProps = useDataAttributes({
		size,
		borderless,
		...dataAttributes
	})
	const { ariaProps, rest } = splitAriaProps(restProps)

	const handleSelect = useCallback(
		(stringValue: string) => {
			if (readOnly) return

			const item = items.find((item) => String(item.value) === stringValue)
			if (item === undefined) return

			onChange(item.value)
		},
		[items, onChange, readOnly]
	)

	const handleEscapeKeyDown = useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault()
			onOpenChange?.(false)
		},
		[onOpenChange]
	)

	const selectedValue = value !== undefined ? String(value) : undefined
	const selectedDisplay = renderValue && value !== undefined ? renderValue(value) : null

	const hasItems = items.length > 0
	const isDisabled = loading || disabled || !hasItems
	const triggerId = id ? `${id}-trigger` : undefined

	const effectivePlaceholder = placeholder ?? (hasItems ? undefined : "선택 가능한 항목이 없습니다")

	// 개발 환경에서 빈 items와 value prop 불일치 경고
	if (process.env.NODE_ENV === "development" && !hasItems && value !== undefined) {
		console.warn(`TSelect: 'value' prop is provided (${value}) but 'items' is empty. The value will be ignored until items are provided.`)
	}

	return (
		<div id={id} className={className} {...rest}>
			<Select name={name} value={selectedValue} onValueChange={handleSelect} disabled={isDisabled} required={required} open={open} onOpenChange={onOpenChange} {...dataProps}>
				<SelectTrigger id={triggerId} disabled={isDisabled} {...ariaProps} aria-invalid={error} data-size={size} data-borderless={borderless}>
					{selectedDisplay ?? <SelectValue placeholder={effectivePlaceholder} />}
					{loading ? <Loader2 aria-hidden="true" className="loader" /> : <TIcon className="icon" name={side === "right" ? "chevronRight" : "dropdownDown"} size={16} />}
				</SelectTrigger>
				<SelectContent
					className={contentClassName}
					align={align}
					alignOffset={alignOffset}
					side={side}
					sideOffset={sideOffset}
					position="popper"
					onEscapeKeyDown={handleEscapeKeyDown}
				>
					<SelectGroup>
						{items.map((item) => (
							<SelectItem key={String(item.value)} value={String(item.value)} disabled={item.disabled}>
								{item.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
			{error && errorMessage && (
				<p className="error-message" role="alert">
					{errorMessage}
				</p>
			)}
		</div>
	)
}

const TSelectMemo = memo(TSelectInner)
TSelectMemo.displayName = "TSelect"

export const TSelect = TSelectMemo as typeof TSelectInner
